import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { COLLAB_UNREACHABLE, declineInvitationByToken, isUnreachable, type GetToken } from '../api';
import { groupsPath } from '../routes';
import { JOIN_REFUSED } from './JoinPage';
import '../pending-invitations.css';

/**
 * `/decline` — the invitation email's decline link (R11, R12, KTD7).
 *
 * **Signed in, and the token never goes near the accept route.** All of `/api` sits behind
 * auth, and an unauthenticated decline would let anyone a link was forwarded to withdraw the
 * invitation. So the link is an SPA route: `invite-token.ts` holds its fragment under a
 * decline key that only this screen reads, and this screen posts it to the decline route and
 * nowhere else. The Worker applies the same address match the accept does.
 *
 * **It asks once.** A decline from an email is one stray click from done, and the admin sees
 * it. The banner's Decline does not ask, because the teacher there is already looking at the
 * invitation; here they arrived from an inbox and have seen nothing yet.
 *
 * **Every refusal is `JoinPage`'s sentence** — no token, an unknown one, someone else's, one
 * already spent (R12). A Worker that could not be reached decided nothing and says so instead.
 */

/** What a teacher reads once the decline has landed. */
export const DECLINED_COPY = 'Invitation declined. The admin will see it.';

export interface DeclinePageProps {
  /** Held under the decline key by `invite-token.ts`; null when there is nothing to decline. */
  token: string | null;
  getToken: GetToken;
  /** Called once the token has been posted and answered, either way. */
  onSpent: () => void;
  /** The teacher is leaving for their groups — by Cancel, or from the result. */
  onFinished: () => void;
}

type Stage = 'confirm' | 'working' | 'declined' | 'refused' | 'unavailable';

export default function DeclinePage({ token, getToken, onSpent, onFinished }: DeclinePageProps) {
  // Decided once, at mount. The token prop goes null the moment `onSpent` clears it, and the
  // screen must keep showing the answer rather than fall back to "no token".
  const [stage, setStage] = useState<Stage>(token === null ? 'refused' : 'confirm');
  // Single-use means single-post, even against a double click landing before the re-render.
  const posted = useRef(false);

  const decline = async () => {
    if (posted.current || token === null) return;
    posted.current = true;
    setStage('working');

    const result = await declineInvitationByToken(getToken, token);
    onSpent();

    if (result.ok) { setStage('declined'); return; }
    setStage(isUnreachable(result.status) ? 'unavailable' : 'refused');
  };

  const back = <Link className="link" to={groupsPath()} onClick={onFinished}>Back to your groups</Link>;

  return (
    <section className="panel" aria-label="Invitation">
      {(stage === 'confirm' || stage === 'working') && (
        <>
          <h2>Decline this invitation?</h2>
          <p>The admin who invited you will see that you declined. They can invite you again later.</p>
          <div className="decline-actions">
            <button type="button" className="primary" disabled={stage === 'working'} onClick={() => { void decline(); }}>
              Decline
            </button>
            <button type="button" disabled={stage === 'working'} onClick={onFinished}>Cancel</button>
          </div>
        </>
      )}

      {stage === 'declined' && (
        <>
          <p className="hint" role="status">{DECLINED_COPY}</p>
          {back}
        </>
      )}

      {(stage === 'refused' || stage === 'unavailable') && (
        <>
          <p className="error" role="alert">{stage === 'refused' ? JOIN_REFUSED : COLLAB_UNREACHABLE}</p>
          {back}
        </>
      )}
    </section>
  );
}
